<template>
  <div>
    <PageTitle
      v-if="gallery?.pageTitle"
      ref="title"
      data-testid="gallery-title"
      :icon-name="galleryIconName"
      :page-title="gallery.pageTitle"
    />
    <GalleryDetails v-if="gallery" :gallery="gallery" />
    <RelatedSearchResults
      v-if="gallery?.onView?.items"
      class="p-gallery__related-results"
      :aria-heading="ariaHeading"
      :heading-level="2"
      :heading="translate('gallery_metadata.results')"
      :items="gallery.onView.items"
      :total="gallery.onView.total"
      :use-route="false"
    />
    <TechnicalData
      v-if="gallery"
      :metadata-fields="gallery.technicalMetadata"
    />
  </div>
</template>

<script>
import GalleryDetails from "@/components/molecules/galleryDetails/index.vue";
import PageTitle from "@/components/atoms/pageTitle/index.vue";
import RelatedSearchResults from "@/components/molecules/relatedSearchResults/index.vue";
import { localizationMixin, useEventBus } from "@thegetty-private/getty-ui";
import { TechnicalData } from "@thegetty-private/getty-ui";

// pinia updates
import { useGalleryStore } from "@/stores/gallery.js";
import { mapStores } from "pinia";

import { useHead } from "@unhead/vue";
import { ref } from "vue";

const metaTitle = ref(null);
const metaDescription = ref(null);

export default {
  name: "GalleryPage",
  components: {
    GalleryDetails,
    PageTitle,
    RelatedSearchResults,
    TechnicalData
  },
  mixins: [localizationMixin],
  inject: ["updatePageTitle"],
  props: {
    /**
     * The gallery object's LOD indexedId
     */
    indexedId: {
      type: String,
      default: undefined
    }
  },
  data() {
    return {
      /**
       * The Aria label for the search results
       */
      ariaHeading: this.translatePart("gallery_page.aria_heading"),
      /**
       * Event bus composable instance
       */
      eventBus: useEventBus()
    };
  },
  computed: {
    ...mapStores(useGalleryStore),
    gallery() {
      return this.galleryStore.getGallery(this.indexedId);
    },
    /**
     * Returns if this gallery is at the Center or the Villa (to display
     * appropriate icon with page title)
     */
    galleryIconName() {
      const location = this.gallery.galleryLocation;

      return location == "OTHER" || !location
        ? undefined
        : location[0] + location.slice(1).toLowerCase();
    },
    /**
     * Returns the gallery description (if available) for the meta tag description
     */
    metaDescription() {
      return this.gallery.description;
    },
    /**
     * Returns the gallery name info split/formatted for the stickyBox
     *
     * @returns {object} ex. { museum: "Getty Center", gallery: "Museum East Pavilion, Gallery E205"}
     */
    stickyBoxData() {
      if (this.gallery.pageTitle) {
        const [museum, ...galleryParts] = this.gallery.pageTitle.split(",");
        const gallery = galleryParts.join(",");

        return { museum, gallery };
      }

      return undefined;
    }
  },
  watch: {
    $route() {
      this.fetchData();
    }
  },
  created() {
    this.fetchData();
  },
  mounted() {
    useHead({
      title: metaTitle,
      meta: [
        {
          name: "description",
          content: metaDescription
        }
      ]
    });
  },
  methods: {
    /**
     * Requests the gallery's Place object
     */
    async fetchData() {
      await this.galleryStore.addGallery(this.indexedId, this.translatePart);

      this.updatePageTitle(this.gallery.pageTitle);
      // set meta tag values
      metaTitle.value = this.gallery.pageTitle;
      metaDescription.value = this.metaDescription;

      // check for metadata to send for analytics
      this.eventBus.emit("PageMetadata", {
        tmsId: this.gallery?.technicalMetadata?.other_ids?.tms_id?.name
      });

      if (this.stickyBoxData) {
        this.eventBus.emit("data-ready", {
          heading: this.stickyBoxData.gallery,
          subheading: this.stickyBoxData.museum
        });
      }
    }
  }
};
</script>

<style lang="scss">
@import "./index.scss";
</style>
